import { useEffect, useMemo, useState } from "react";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { fmtISTDate } from "../lib/utils";
import { Button } from "../components/ui/button";
import { CheckCircle, X, Clock, PencilSimple, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Overtime Approvals — HR reviews biometric-generated OT rows before they
 * flow into payroll. Supports per-row Approve/Reject and bulk
 * Approve / Reject / Edit for selected rows.
 */
export default function OvertimeApprovals() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(new Set());
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [editModal, setEditModal] = useState(false);
  const [editRate, setEditRate] = useState("");
  const [editHours, setEditHours] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/overtime/pending", { params: { status_filter: filter } });
      setRows(Array.isArray(data) ? data : []);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    setSelected(selected.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  };

  const approve = async (id) => {
    try {
      await api.post(`/overtime/${id}/approve`);
      toast.success("OT approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try {
      await api.post(`/overtime/${id}/reject`, { reason: "" });
      toast.success("OT rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkApprove = async () => {
    setBulkMenuOpen(false);
    if (!selected.size) return;
    if (!window.confirm(`Approve ${selected.size} selected OT row(s)?`)) return;
    try {
      const { data } = await api.post("/overtime/bulk-approve", { ids: [...selected] });
      toast.success(`Approved ${data.approved} OT rows`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const bulkReject = async () => {
    setRejectModal(false);
    setBulkMenuOpen(false);
    if (!selected.size) return;
    try {
      const { data } = await api.post("/overtime/bulk-reject", {
        ids: [...selected],
        reason: rejectReason.trim() || null,
      });
      toast.success(`Rejected ${data.rejected} OT rows`);
      setRejectReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const bulkEdit = async () => {
    setEditModal(false);
    setBulkMenuOpen(false);
    if (!selected.size) return;
    const payload = { ids: [...selected] };
    if (editRate !== "") payload.rate_multiplier = parseFloat(editRate);
    if (editHours !== "") payload.ot_hours = parseFloat(editHours);
    if (payload.rate_multiplier === undefined && payload.ot_hours === undefined) {
      toast.error("Enter a rate or hours to update");
      return;
    }
    try {
      const { data } = await api.post("/overtime/bulk-edit", payload);
      toast.success(`Updated ${data.updated} OT rows`);
      setEditRate(""); setEditHours("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalHours = rows.reduce((sum, r) => sum + (r.ot_hours || 0), 0);
  const selectedRows = useMemo(() => rows.filter(r => selected.has(r.id)), [rows, selected]);
  const selectedHours = selectedRows.reduce((s, r) => s + (r.ot_hours || 0), 0);

  return (
    <AppShell title="Overtime Approvals" moduleKey="people">
      <SectionCard
        title="Pending overtime from biometric imports"
        subtitle={`${rows.length} rows · ${totalHours.toFixed(2)} total hours`}
        action={
          <div className="flex gap-2 items-center">
            <select
              data-testid="ot-status-filter"
              className="border border-zinc-300 rounded h-9 text-sm px-2"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            {filter === "pending" && (
              <div className="relative">
                <Button
                  size="sm"
                  onClick={() => selected.size > 0 && setBulkMenuOpen(!bulkMenuOpen)}
                  disabled={selected.size === 0}
                  data-testid="ot-bulk-actions-btn"
                  title={selected.size === 0 ? "Tick one or more rows to enable bulk actions" : ""}
                >
                  Bulk actions{selected.size > 0 ? ` (${selected.size})` : ""} <CaretDown size={12} className="ml-1"/>
                </Button>
                {bulkMenuOpen && selected.size > 0 && (
                  <div
                    className="absolute right-0 top-10 z-20 w-56 bg-white border border-zinc-200 rounded-md shadow-lg py-1"
                    data-testid="ot-bulk-menu"
                  >
                    <div className="px-3 py-2 text-xs text-zinc-500 border-b border-zinc-100">
                      {selected.size} selected · {selectedHours.toFixed(2)} hrs
                    </div>
                    <button
                      data-testid="ot-bulk-approve"
                      onClick={bulkApprove}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center gap-2 text-emerald-700"
                    >
                      <CheckCircle size={14}/> Approve selected
                    </button>
                    <button
                      data-testid="ot-bulk-edit-open"
                      onClick={() => { setBulkMenuOpen(false); setEditModal(true); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center gap-2"
                    >
                      <PencilSimple size={14}/> Edit rate / hours…
                    </button>
                    <button
                      data-testid="ot-bulk-reject-open"
                      onClick={() => { setBulkMenuOpen(false); setRejectModal(true); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-rose-50 flex items-center gap-2 text-rose-700"
                    >
                      <X size={14}/> Reject selected
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        }
      >
        {filter === "pending" && rows.length > 0 && (
          <div className="mb-3 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm" data-testid="ot-bulk-hint">
            <span className="text-amber-900">
              {selected.size === 0
                ? "Tip: tick the header checkbox to select all rows, then use Bulk actions to approve, edit or reject in one click."
                : <>Selected <b>{selected.size}</b> of {rows.length} rows · {selectedHours.toFixed(2)} hrs — use <b>Bulk actions ▾</b> on the top right.</>}
            </span>
            {selected.size > 0 && (
              <button
                data-testid="ot-clear-selection"
                onClick={() => setSelected(new Set())}
                className="text-xs text-amber-800 underline hover:no-underline"
              >Clear selection</button>
            )}
          </div>
        )}
        <div className="overflow-x-auto border border-zinc-200 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                {filter === "pending" && (
                  <th className="w-8 py-2 px-3">
                    <input
                      type="checkbox"
                      data-testid="ot-select-all"
                      checked={selected.size === rows.length && rows.length > 0}
                      onChange={toggleAll}
                    />
                  </th>
                )}
                <th className="text-left py-2 px-3 font-medium">Date</th>
                <th className="text-left py-2 px-3 font-medium">Employee</th>
                <th className="text-right py-2 px-3 font-medium">OT hours</th>
                <th className="text-right py-2 px-3 font-medium">Rate</th>
                <th className="text-left py-2 px-3 font-medium">Status</th>
                {filter === "pending" && <th className="text-right py-2 px-3 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  {filter === "pending" && (
                    <td className="py-2 px-3">
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                        data-testid={`ot-select-${r.id}`}
                      />
                    </td>
                  )}
                  <td className="py-2 px-3 font-mono text-xs" title={r.date}>{fmtISTDate(r.date)}</td>
                  <td className="py-2 px-3">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-xs text-zinc-500">{r.employee_code}</div>
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-orange-700">
                    <Clock size={12} className="inline mr-1"/>{r.ot_hours}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{r.rate_multiplier}×</td>
                  <td className="py-2 px-3">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      r.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                      r.status === "rejected" ? "bg-rose-100 text-rose-800" :
                      "bg-amber-100 text-amber-800"
                    }`}>{r.status}</span>
                  </td>
                  {filter === "pending" && (
                    <td className="py-2 px-3 text-right">
                      <Button size="sm" variant="outline" className="h-7 text-xs mr-1"
                        onClick={() => approve(r.id)} data-testid={`ot-approve-${r.id}`}>
                        <CheckCircle size={12}/> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700"
                        onClick={() => reject(r.id)} data-testid={`ot-reject-${r.id}`}>
                        <X size={12}/> Reject
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={filter === "pending" ? 7 : 6} className="text-center py-8 text-zinc-500">
                  No {filter} overtime rows. Run a biometric import to generate pending OT.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Reject-with-reason modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="ot-bulk-reject-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">Reject {selected.size} OT rows</h3>
            <p className="text-sm text-zinc-500 mb-4">{selectedHours.toFixed(2)} hours will be rejected. Reason is optional but recommended.</p>
            <textarea
              data-testid="ot-bulk-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="e.g. Not pre-approved by manager"
              className="w-full border border-zinc-300 rounded p-2 text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setRejectModal(false)}>Cancel</Button>
              <Button size="sm" onClick={bulkReject} data-testid="ot-bulk-reject-confirm" className="bg-rose-600 hover:bg-rose-700 text-white">
                Reject {selected.size}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk edit modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="ot-bulk-edit-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">Edit {selected.size} OT rows</h3>
            <p className="text-sm text-zinc-500 mb-4">Leave a field blank to keep it unchanged.</p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-xs font-medium text-zinc-600">Rate multiplier (0–5)</label>
                <input
                  data-testid="ot-bulk-edit-rate"
                  type="number" step="0.1" min="0" max="5"
                  value={editRate}
                  onChange={(e) => setEditRate(e.target.value)}
                  placeholder="e.g. 2.0"
                  className="w-full border border-zinc-300 rounded p-2 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-600">OT hours (0–24)</label>
                <input
                  data-testid="ot-bulk-edit-hours"
                  type="number" step="0.25" min="0" max="24"
                  value={editHours}
                  onChange={(e) => setEditHours(e.target.value)}
                  placeholder="e.g. 8"
                  className="w-full border border-zinc-300 rounded p-2 text-sm mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditModal(false)}>Cancel</Button>
              <Button size="sm" onClick={bulkEdit} data-testid="ot-bulk-edit-confirm">Apply to {selected.size}</Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
